import { collection, doc, getDoc, getDocFromServer, getDocs, query } from "firebase/firestore";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useUserContext } from "../../../context/UserProvider";
import { app, db } from "../../../firebase";
import CompactLeaderboard from "./CompactLeaderboard";
import { isCompactLeaderboardEnabled } from "./compactLeaderboardData";
import { SprintIcon } from "../../util/Icons";
import { useThemeContext } from "../../../context/ThemeProvider";
import ChallengeMapSection, { hasChallengeMap } from "./ChallengeMapSection";
import LeaderboardHeader from "./LeaderboardHeader";
import LeaderboardLoadingState from "./LeaderboardLoadingState";
import { getRaceSizeBucket, startLeaderboardLoadTrace } from "./leaderboardPerformance";

export default function LeaderBoardPage() {
  const { user, userRaces } = useUserContext();
  const { raceId } = useParams();
  const selectedRace = userRaces?.get(`price_${raceId}`);
  const key = `${user?.uid}:${selectedRace?.legId}`;
  return selectedRace ? <RaceLeaderboard key={key} selectedRace={selectedRace} uid={user?.uid} /> : <LegacyLeaderboard key={key} />;
}

export function RaceLeaderboard({ selectedRace, uid }) {
  const [selection, setSelection] = useState(null);
  const [failed, setFailed] = useState(false);
  const fallback = useCallback(() => setFailed(true), []);
  const raceId = selectedRace.legId;
  useEffect(() => {
    let active = true;
    // Include the readiness read in load duration and reuse its metadata in
    // either renderer, keeping the initial-load read metric accurate.
    const trace = startLeaderboardLoadTrace({ raceId });
    getDocFromServer(doc(db, "races", raceId)).then((race) => {
      if (active) setSelection({ raceData: race.exists() ? race.data() : null, trace });
    }).catch(() => {
      if (active) setSelection({ raceData: null, trace });
    });
    return () => { active = false; trace.stop("cancelled"); };
  }, [raceId]);
  if (!selection) return <LeaderboardLoadingState />;
  const compact = !failed && isCompactLeaderboardEnabled({
    raceData: selection.raceData, uid, projectId: app.options.projectId,
  });
  if (compact) {
    return <CompactLeaderboard selectedRace={selectedRace} onFallback={fallback}
      initialRaceData={selection.raceData} initialTrace={selection.trace} />;
  }
  return <LegacyLeaderboard initialRaceData={selection.raceData} initialTrace={failed ? null : selection.trace} />;
}

function LegacyLeaderboard({ initialRaceData = null, initialTrace = null }) {
  const { colors: COLORS } = useThemeContext();
  const { user: USER, userRaces } = useUserContext();
  const { raceId } = useParams();
  const [raceUsers, setRaceUsers] = useState(new Map());
  const selectedUsersPanel = useRef();
  const myTimePanel = useRef();
  const leaderboardTrace = useRef(null);
  const [raceData, setRaceData] = useState(null);
  const [raceDataStatus, setRaceDataStatus] = useState("idle");
  const [raceUsersStatus, setRaceUsersStatus] = useState("loading");
  const [leaderboardLoadMetrics, setLeaderboardLoadMetrics] = useState(null);

  const selectedRace = userRaces?.get(`price_${raceId}`);

  let raceUsersArray = [];
  if (raceUsers) {
    raceUsers.forEach((user, id) => {
      raceUsersArray.push({ id: id, data: user });
    });
  }
  raceUsersArray = raceUsersArray.sort((a, b) => {
    let aDistance = parseFloat(a.data?.totalDistance) || 0;
    let bDistance = parseFloat(b.data?.totalDistance) || 0;
    return bDistance - aDistance;
  });

  raceUsersArray = raceUsersArray.sort((a, b) => {
    if (!a.data.completedDate && !b.data.completedDate) return 0;
    if (!a.data.completedDate) return 1;
    if (!b.data.completedDate) return -1;
    return parseFloat(a.data.completedDate) - parseFloat(b.data.completedDate);
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (!selectedRace?.legId) return undefined;

    let active = true;
    const selectedRaceLegId = selectedRace.legId;
    const trace = initialTrace || startLeaderboardLoadTrace({
      raceId: selectedRaceLegId,
      dataSource: "legacy",
    });
    leaderboardTrace.current = trace;
    setRaceUsers(null);
    setRaceData(null);
    setRaceDataStatus("idle");
    setRaceUsersStatus("loading");
    setLeaderboardLoadMetrics(null);

    async function getRaceData() {
      if (!active) return;
      setRaceDataStatus("loading");
      try {
        const raceDoc = initialRaceData ? { data: () => initialRaceData } : await getDoc(doc(db, "races", selectedRaceLegId));
        if (!active) return;
        setRaceData(raceDoc.data());
        setRaceDataStatus("success");
      } catch (err) {
        console.log(err.message);
        if (active) setRaceDataStatus("error");
      }
    }

    async function getAllUsers() {
      try {
        const raceUsersQuery = query(collection(db, "races", selectedRaceLegId, "subscribedUsers"));
        const raceUsersDocs = await getDocs(raceUsersQuery);
        if (!active) return;
        const raceUsersMap = new Map();

        raceUsersDocs.forEach((user) => {
          if (user.data().teamName !== "noTeam") {
            const userData = user.data();
            const existingTeam = raceUsersMap.get(userData.teamName);
            const teamData = {
              firstName: userData.teamName,
              totalDistance: userData.totalDistance + (existingTeam?.totalDistance || 0) || 0,
              isTeam: true,
              bibNumber: existingTeam?.bibNumber || Math.random(),
              membersCount: (existingTeam?.membersCount || 0) + 1,
            };
            if (userData.completedDate) {
              teamData.completedDate = userData.completedDate;
            }
            raceUsersMap.set(userData.teamName, teamData);
          } else {
            raceUsersMap.set(user.id, user.data());
          }
        });

        setRaceUsers(raceUsersMap);
        setRaceUsersStatus("success");
        setLeaderboardLoadMetrics({
          documentCount: raceUsersDocs.size,
          rowCount: raceUsersMap.size,
          result: "success",
        });
      } catch (err) {
        console.log(err.message);
        if (!active) return;
        setRaceUsersStatus("error");
        setLeaderboardLoadMetrics({
          documentCount: 0,
          rowCount: 0,
          result: "error",
        });
      }
    }

    getRaceData();
    getAllUsers();

    return () => {
      active = false;
      trace.stop("cancelled");
      if (leaderboardTrace.current === trace) {
        leaderboardTrace.current = null;
      }
    };
  }, [selectedRace?.legId, initialRaceData, initialTrace]);

  useEffect(() => {
    if (!leaderboardTrace.current || !leaderboardLoadMetrics) return;
    if (raceDataStatus !== "success" && raceDataStatus !== "error") return;

    const trace = leaderboardTrace.current;
    trace.putMetric("participant_docs", leaderboardLoadMetrics.documentCount);
    trace.putMetric(
      "leaderboard_reads",
      leaderboardLoadMetrics.documentCount + (raceDataStatus === "success" ? 1 : 0)
    );
    trace.putMetric("leaderboard_rows", leaderboardLoadMetrics.rowCount);
    trace.putAttribute("race_size", getRaceSizeBucket(leaderboardLoadMetrics.documentCount));
    trace.putAttribute(
      "has_map",
      raceDataStatus === "error" ? "unknown" : hasChallengeMap(raceData?.map) ? "yes" : "no"
    );
    trace.stop(leaderboardLoadMetrics.result === "success" && raceDataStatus === "success" ? "success" : "error");
    leaderboardTrace.current = null;
  }, [leaderboardLoadMetrics, raceData?.map, raceDataStatus]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!myTimePanel.current) {
          return;
        }
        if (entry.isIntersecting) {
          myTimePanel.current.style.opacity = 0;
        } else {
          myTimePanel.current.style.opacity = 1;
          if (entry.boundingClientRect.bottom > 100) {
            myTimePanel.current.style.bottom = 0;
            myTimePanel.current.style.top = "auto";
          } else {
            myTimePanel.current.style.top = 0;
            myTimePanel.current.style.bottom = "auto";
          }
        }
      },
      { threshold: 0.5 }
    );

    if (selectedUsersPanel.current && myTimePanel) {
      observer.observe(selectedUsersPanel.current);
    }

    return () => {
      if (selectedUsersPanel.current) {
        observer.unobserve(selectedUsersPanel.current);
      }
    };
  }, [raceUsersArray]);

  if (!!userRaces && !selectedRace) {
    return <Navigate to="/" replace={true} />;
  }
  if (!selectedRace) {
    return;
  }

  const raceDistance = parseInt(selectedRace.raceInfo.distance);
  return (
    <div className="relative mx-auto mt-5 h-full w-full max-w-3xl">
      <LeaderboardHeader selectedRace={selectedRace} />

      {raceUsersStatus === "loading" && <LeaderboardLoadingState />}

      {raceUsersStatus === "error" && (
        <div className="my-8 rounded-xl border border-red-200 bg-red-50 px-4 py-6 text-center" role="alert">
          <p className="m-0 font-semibold text-red-800">We couldn’t load the leaderboard.</p>
          <p className="mb-0 mt-1 text-sm text-red-700">Please refresh the page and try again.</p>
        </div>
      )}

      {raceUsersStatus === "success" && raceUsers && (
        <>
          <ChallengeMapSection
            key={selectedRace.legId}
            racersData={raceUsers}
            ownerData={selectedRace}
            mapName={raceData?.map}
          />

          <div className="leaderboard relative">
            {raceUsersArray.map((user, index) => {
          let userDistance = parseFloat(user.data.totalDistance) || 0;
          let progress = (userDistance * 100) / raceDistance;
          const barProgress = progress <= 100 ? progress : 100;
          const isOwnTime = user.id == USER.uid;
          const isOwnTeamTime = user.data.teamName !== "noTeam" && selectedRace?.teamName === user.id;
          const style = isOwnTime || isOwnTeamTime ? { backgroundColor: `${COLORS.blue}23` } : {};
          return (
            <div key={user.data.bibNumber}>
              <div
                ref={isOwnTime || isOwnTeamTime ? selectedUsersPanel : null}
                className={`relative pb-4 border-b-2 py-1 mb-3 mx-1`}
                style={style}
                key={user.data.bibNumber}
              >
                <p className="font-semibold mb-2 px-1">
                  #{index + 1} {user.data.firstName} {user.data.lastName}{" "}
                  {user.data?.isTeam ? (
                    <span className="text-green-500 text-xs">Team of {user.data.membersCount}</span>
                  ) : (
                    <span className="text-green-500 text-xs">#{user.data.bibNumber}</span>
                  )}
                  {user.data.completedDate && (
                    <>
                      {" "}
                      <span className="m-0 text-sm font-thin">
                        {new Date(user.data.completedDate).toLocaleString()}
                      </span>{" "}
                    </>
                  )}
                </p>
                <div className="grid grid-cols-2 gap-3 px-2">
                  <div>
                    <p className="mb-0 ">Distance</p>
                    <p className="mb-1">{userDistance.toFixed(2)}Km</p>
                  </div>
                  <div>
                    <p className={"mb-0 ".concat(progress >= 100 ? "text-green-600" : "")}>
                      {progress >= 100 ? "Completed!" : "Progress"}
                    </p>
                    <p className="mb-1">% {progress.toFixed(2)}</p>
                  </div>
                </div>
                <div className="absolute bottom-1 bg-slate-100 w-full h-5">
                  <div className="abslute h-full bg-green-200" style={{ width: `${barProgress}%` }}></div>
                  <div
                    className="absolute h-full w-5 top-0  "
                    style={{ left: `clamp(0px,calc(${barProgress}% - 10px), calc(100% - 20px))` }}
                  >
                    <SprintIcon size={20} />
                  </div>
                </div>
              </div>
              {/* `SELF` */}
              {(isOwnTime || isOwnTeamTime) && (
                <div
                  ref={myTimePanel}
                  className={`fixed pointer-events-none pb-4 w-[calc(100%-2rem)] max-w-3xl border-b-2 py-1 mx-auto z-10 bottom-[env(safe-area-inset-bottom]  backdrop-blur-md`}
                  style={{ backgroundColor: `${COLORS.blue}23` }}
                  key={`myTime_${user.id}`}
                >
                  <p className="font-semibold mb-2 px-1">
                    #{index + 1} {user.data.firstName} {user.data.lastName}{" "}
                    {user.data?.isTeam ? (
                      <span className="text-green-500 text-xs">Team of {user.data.membersCount}</span>
                    ) : (
                      <span className="text-green-500 text-xs">#{user.data.bibNumber}</span>
                    )}
                    {user.data.completedDate && (
                      <>
                        {" "}
                        <span className="m-0 text-sm font-thin">
                          {new Date(user.data.completedDate).toLocaleString()}
                        </span>{" "}
                      </>
                    )}
                  </p>
                  <div className="grid grid-cols-2 gap-3 px-2">
                    <div>
                      <p className="mb-0 ">Distance</p>
                      <p className="mb-1">{userDistance.toFixed(2)}Km</p>
                    </div>
                    <div>
                      <p className={"mb-0 ".concat(progress >= 100 ? "text-green-600" : "")}>
                        {progress >= 100 ? "Completed!" : "Progress"}
                      </p>
                      <p className="mb-1">% {progress.toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="absolute bottom-1 bg-slate-100 w-full h-5">
                    <div className="abslute h-full bg-green-200" style={{ width: `${barProgress}%` }}></div>
                    <div
                      className="absolute h-full w-5 top-0  "
                      style={{ left: `clamp(0px,calc(${barProgress}% - 10px), calc(100% - 20px))` }}
                    >
                      <SprintIcon size={20} />
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
            })}
          </div>
        </>
      )}
    </div>
  );
}
